import axios from "axios";
import { Employee, OvertimeStats, WorkStats, VacationRequest, OvertimePermission } from "../types";

const API_BASE_URL = "/api";

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

export const getEmployees = async (): Promise<Employee[]> => {
  const response = await axios.get(`${API_BASE_URL}/overtime`);
  return response.data;
};

export const getCurrentUserEmployee = async (): Promise<Employee> => {
  const response = await axios.get(`${API_BASE_URL}/overtime/me`);
  return response.data;
};

export const getEmployeeDetails = async (
  employeeName: string,
): Promise<Employee> => {
  const response = await axios.get(
    `${API_BASE_URL}/overtime/employee/${encodeURIComponent(employeeName)}`,
  );
  return response.data;
};

export const getEmployeeWorkStats = async (
  employeeName: string,
): Promise<WorkStats[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/overtime/${encodeURIComponent(employeeName)}`,
  );
  return response.data;
};

export const getEmployeeOvertimeStats = async (
  employeeName: string,
): Promise<OvertimeStats[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/overtime/${encodeURIComponent(employeeName)}/stats`,
  );
  return response.data;
};

export const hasOvertimePermission = async (
  userId: string,
  date: string,
): Promise<boolean> => {
  const response = await axios.get(
    `${API_BASE_URL}/overtime/permissions/check?userId=${encodeURIComponent(userId)}&date=${date}`,
  );
  return response.data;
};

// Vacation Request API methods
export const createVacationRequest = async (
  startDate: string,
  endDate: string,
  notes?: string
): Promise<VacationRequest> => {
  const response = await axios.post(
    `${API_BASE_URL}/vacation-requests`,
    { startDate, endDate, notes }
  );
  return response.data;
};

export const getMyVacationRequests = async (): Promise<VacationRequest[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/vacation-requests/my`);

    // If we get HTML instead of JSON, it means we're not authenticated
    if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
      console.error('API returned HTML instead of JSON - authentication issue');
      return [];
    }

    // Ensure we return an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching vacation requests:', error);
    return [];
  }
};

export const getEmployeeVacationRequests = async (
  employeeUserId: string
): Promise<VacationRequest[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/vacation-requests/employee/${encodeURIComponent(employeeUserId)}`
  );
  return response.data;
};

export const getPendingVacationRequests = async (): Promise<VacationRequest[]> => {
  const response = await axios.get(`${API_BASE_URL}/vacation-requests/pending`);
  return response.data;
};

export const getVacationRequest = async (
  requestId: number
): Promise<VacationRequest> => {
  const response = await axios.get(`${API_BASE_URL}/vacation-requests/${requestId}`);
  return response.data;
};

export const approveVacationRequest = async (
  requestId: number
): Promise<VacationRequest> => {
  const response = await axios.post(
    `${API_BASE_URL}/vacation-requests/${requestId}/approve`
  );
  return response.data;
};

export const rejectVacationRequest = async (
  requestId: number,
  reason: string
): Promise<VacationRequest> => {
  const response = await axios.post(
    `${API_BASE_URL}/vacation-requests/${requestId}/reject`,
    { reason }
  );
  return response.data;
};

export const cancelVacationRequest = async (
  requestId: number
): Promise<VacationRequest> => {
  const response = await axios.post(
    `${API_BASE_URL}/vacation-requests/${requestId}/cancel`
  );
  return response.data;
};

// Admin-only: manually manage employee vacation entries
export const adminCreateVacationEntry = async (
  employeeUserId: string,
  startDate: string,
  endDate: string,
  notes?: string
): Promise<VacationRequest> => {
  const response = await axios.post(
    `${API_BASE_URL}/vacation-requests/admin/employee/${encodeURIComponent(employeeUserId)}`,
    { startDate, endDate, notes }
  );
  return response.data;
};

export const adminUpdateVacationRequest = async (
  requestId: number,
  startDate: string,
  endDate: string,
  notes?: string
): Promise<VacationRequest> => {
  const response = await axios.put(
    `${API_BASE_URL}/vacation-requests/${requestId}`,
    { startDate, endDate, notes }
  );
  return response.data;
};

export const adminDeleteVacationRequest = async (
  requestId: number
): Promise<void> => {
  await axios.delete(`${API_BASE_URL}/vacation-requests/${requestId}`);
};

export const deleteEmployeeCache = async (
  employeeName: string
): Promise<void> => {
  await axios.delete(
    `${API_BASE_URL}/overtime/${encodeURIComponent(employeeName)}/cache`
  );
};

export const getHolidays = async (): Promise<Record<string, string>> => {
  const response = await axios.get(`${API_BASE_URL}/holidays`);
  return response.data;
};

// Overtime Permission API methods
export const getEmployeeOvertimePermissions = async (
  employeeUserId: string
): Promise<OvertimePermission[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/overtime/permissions/employee/${encodeURIComponent(employeeUserId)}`
  );
  return response.data;
};

export const createOvertimePermission = async (
  userId: string,
  startDate: string,
  endDate: string | null
): Promise<OvertimePermission> => {
  const params = new URLSearchParams();
  params.append("userId", userId);
  params.append("startDate", startDate);
  if (endDate) {
    params.append("endDate", endDate);
  }

  const response = await axios.post(
    `${API_BASE_URL}/overtime/permissions`,
    params,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    }
  );
  return response.data;
};

export const updateOvertimePermission = async (
  id: number,
  startDate: string,
  endDate: string | null
): Promise<OvertimePermission> => {
  const params = new URLSearchParams();
  params.append("startDate", startDate);
  if (endDate) {
    params.append("endDate", endDate);
  }

  const response = await axios.put(
    `${API_BASE_URL}/overtime/permissions/${id}`,
    params,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    }
  );
  return response.data;
};

export const deleteOvertimePermission = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE_URL}/overtime/permissions/${id}`);
};

// Password reset functionality
export const validatePasswordResetToken = async (
  token: string
): Promise<{ valid: boolean; reason?: string }> => {
  const response = await axios.get(
    `/api/auth/password-reset/validate/${token}`
  );
  return response.data;
};

export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<void> => {
  const params = new URLSearchParams();
  params.append("token", token);
  params.append("newPassword", newPassword);
  await axios.post("/api/auth/password-reset/reset", params, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
};

export const generatePasswordResetUrl = async (
  employeeName: string
): Promise<{ resetUrl: string; expiresAt: string }> => {
  const response = await axios.post("/api/admin/passwords/generate-reset-url", {
    employeeName,
  });
  return response.data;
};